/**
 * NodeMap — force-directed network graph with genealogical lenses.
 *
 * Three things make this useful beyond "a pretty blob":
 *
 *   1. Time axis (vertical). Y is pinned by birth year so generations form
 *      horizontal bands. Earlier ancestors sit at the top, modern descendants
 *      at the bottom. Persons with no birth date fall to the midline.
 *
 *   2. Branch clustering + centrality. When a focal person is set, every
 *      other node is classified as self / ancestor / descendant / collateral
 *      / in-law by walking the relationship graph. Stroke colour encodes
 *      the side; fill still encodes gender. Node radius scales with degree
 *      — the "keystone" relatives (marriage connectors, common ancestors)
 *      visibly pop out.
 *
 *   3. Search + shortest-path. A name search filters the visible labels.
 *      Clicking two persons in path-find mode highlights the shortest
 *      relationship chain between them (BFS over the undirected graph).
 *
 * Interaction summary:
 *   - Hover a node → focus its neighbourhood (dim the rest)
 *   - Scroll to zoom, drag empty space to pan, double-click to recentre
 *   - Labels appear at zoom > 1.4× or on hover
 *   - Search input top-left filters labels by name
 *   - "Route" button (or hold Shift + click 2 nodes) → shortest path
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'

const NODE_RADIUS_MIN = 12
const NODE_RADIUS_MAX = 26
const COLLIDE_PADDING = 6   // added to each node's own radius

// Side-of-family colours for the stroke ring. Fill stays gender-based.
const SIDE_STROKE = {
  self:        '#1a1a1a',
  ancestor:    '#2563eb',  // blue: upstream
  descendant:  '#b45309',  // amber: downstream
  collateral:  '#059669',  // green: siblings, cousins, etc.
  inlaw:       '#9333ea',  // purple: reached only via a partner edge
  unrelated:   '#ffffff',  // no focal → neutral
}

export default function NodeMap({ persons, relationships, focalPersonId, onPersonClick }) {
  const svgRef = useRef(null)
  const [search, setSearch] = useState('')
  const [routing, setRouting] = useState(false)
  const [routeMsg, setRouteMsg] = useState(null)

  // Computed graph metadata used by the renderer. Kept in useMemo so we don't
  // recompute on every search keystroke.
  const meta = useMemo(() => buildGraphMeta(persons, relationships, focalPersonId),
                       [persons, relationships, focalPersonId])

  useEffect(() => {
    if (!svgRef.current) return
    if (!persons?.length) return
    try {
      renderNodeMap(svgRef.current, meta, {
        onPersonClick,
        search,
        routing,
        onRouteMessage: setRouteMsg,
      })
    } catch (err) {
      console.error('NodeMap render failed', err)
      d3.select(svgRef.current).selectAll('*').remove()
    }
  }, [meta, search, routing])

  return (
    <div className="node-map">
      <div className="node-map__toolbar">
        <input
          className="node-map__search"
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search names…"
        />
        <button
          className={`node-map__route-btn ${routing ? 'node-map__route-btn--on' : ''}`}
          onClick={() => { setRouting(r => !r); setRouteMsg(null) }}
          title="Click two people to trace the shortest relationship path"
        >
          {routing ? '✕ Cancel route' : '↔ Find route'}
        </button>
        {routeMsg && <span className="node-map__route-msg">{routeMsg}</span>}
      </div>

      <div className="node-map__legend">
        {focalPersonId ? (
          <>
            <LegendDot color={SIDE_STROKE.self}>self</LegendDot>
            <LegendDot color={SIDE_STROKE.ancestor}>ancestors</LegendDot>
            <LegendDot color={SIDE_STROKE.descendant}>descendants</LegendDot>
            <LegendDot color={SIDE_STROKE.collateral}>collateral</LegendDot>
            <LegendDot color={SIDE_STROKE.inlaw}>in-laws</LegendDot>
          </>
        ) : (
          <span className="node-map__legend-hint">
            Open a person from the tree to enable branch colouring.
          </span>
        )}
      </div>

      <svg ref={svgRef} width="100%" height="100%" />
    </div>
  )
}

function LegendDot({ color, children }) {
  return (
    <span className="node-map__legend-dot">
      <span style={{ background: '#e5e0d5', borderColor: color }} />
      {children}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Graph metadata
// ─────────────────────────────────────────────────────────────────────────────

function buildGraphMeta(persons, relationships, focalPersonId) {
  const personIds = new Set(persons.map(p => p.id))

  // Adjacency keyed by id. Each edge records type + partner(bool) so side
  // classification can see in-law walks.
  const adj = new Map(persons.map(p => [p.id, []]))
  const links = []
  for (const r of relationships) {
    if (!personIds.has(r.person_a_id) || !personIds.has(r.person_b_id)) continue
    adj.get(r.person_a_id).push({ neighbour: r.person_b_id, type: r.type, fromSide: 'a' })
    adj.get(r.person_b_id).push({ neighbour: r.person_a_id, type: r.type, fromSide: 'b' })
    links.push({ source: r.person_a_id, target: r.person_b_id, type: r.type })
  }

  const degree = new Map(persons.map(p => [p.id, adj.get(p.id).length]))

  // Time axis scale. Skip persons without a year so missing data doesn't
  // distort the extent.
  const years = persons
    .map(p => parseYear(p.birth_date))
    .filter(y => y != null)
  const minYear = years.length ? Math.min(...years) : null
  const maxYear = years.length ? Math.max(...years) : null

  // Side-of-family classification. When there's no focal person everyone is
  // 'unrelated' and stroke is neutral.
  const sides = new Map(persons.map(p => [p.id, 'unrelated']))
  if (focalPersonId && personIds.has(focalPersonId)) {
    classifySides(focalPersonId, persons, adj, sides)
  }

  // Nodes array — flatten the person with its computed metadata.
  const nodes = persons.map(p => ({
    id: p.id,
    label: [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown',
    gender: p.gender,
    birthYear: parseYear(p.birth_date),
    degree: degree.get(p.id) || 0,
    side: sides.get(p.id) || 'unrelated',
  }))

  // Neighbours for hover-highlight (ids only — the walk-side classification
  // is separate metadata above).
  const neighbours = new Map(persons.map(p => [p.id, new Set()]))
  for (const l of links) {
    neighbours.get(l.source).add(l.target)
    neighbours.get(l.target).add(l.source)
  }

  return { nodes, links, neighbours, minYear, maxYear, focalPersonId, adj }
}

/**
 * BFS from the focal person. Each neighbour is classified by how we reached
 * it and what edge types are on the walk:
 *
 *   - focal itself → 'self'
 *   - reached only via parent_child edges going up → 'ancestor'
 *   - reached only via parent_child edges going down → 'descendant'
 *   - reached via sibling edges, or parent_child then sideways → 'collateral'
 *   - reached through a partner edge at any point → 'inlaw'
 *
 * This is approximate but catches the common mental buckets.
 */
function classifySides(focalId, persons, adj, sides) {
  sides.set(focalId, 'self')
  // personId → 'ancestor' | 'descendant' | null (for focal or collateral yet)
  const direction = new Map()
  const personById = new Map(persons.map(p => [p.id, p]))

  // Level-order walk. State is { id, via } where `via` is the classification
  // of how the CALLER reached this node. We keep the first classification
  // we see — BFS guarantees that's the shortest chain, which is the best
  // descriptor of the relationship.
  const queue = [{ id: focalId, via: 'self' }]
  const visited = new Set([focalId])

  while (queue.length) {
    const { id, via } = queue.shift()
    for (const edge of adj.get(id) || []) {
      if (visited.has(edge.neighbour)) continue
      visited.add(edge.neighbour)

      // Decide how the neighbour is classified relative to the focal person,
      // based on the edge type and where we came from.
      let next
      if (edge.type === 'partner') {
        // Focal's direct spouse is still "self-adjacent" but count as in-law.
        // Anyone reached through any partner edge is an in-law in this model.
        next = (via === 'self' || via === 'inlaw') ? 'inlaw' : 'inlaw'
      } else if (edge.type === 'parent_child') {
        // Direction is determined by which endpoint we came from: if we're
        // walking from child to parent that's "up" (ancestor path). But at
        // this layer we don't know who's the parent without the raw row, so
        // inspect the person's birth_year to infer direction when available
        // — an older neighbour is the ancestor.
        const meYear = parseYear(personById.get(id)?.birth_date)
        const nbYear = parseYear(personById.get(edge.neighbour)?.birth_date)
        let goingUp
        if (meYear != null && nbYear != null) goingUp = nbYear < meYear
        else goingUp = via === 'ancestor' || via === 'self' // fallback: stay on same side
        if (via === 'self') next = goingUp ? 'ancestor' : 'descendant'
        else if (via === 'ancestor') next = goingUp ? 'ancestor' : 'collateral'
        else if (via === 'descendant') next = goingUp ? 'collateral' : 'descendant'
        else if (via === 'inlaw') next = 'inlaw'
        else next = 'collateral'
      } else if (edge.type === 'sibling') {
        next = (via === 'self') ? 'collateral' : via === 'inlaw' ? 'inlaw' : 'collateral'
      } else {
        next = via === 'inlaw' ? 'inlaw' : 'collateral'
      }

      sides.set(edge.neighbour, next)
      queue.push({ id: edge.neighbour, via: next })
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Render
// ─────────────────────────────────────────────────────────────────────────────

function renderNodeMap(svg, meta, { onPersonClick, search, routing, onRouteMessage }) {
  const el = d3.select(svg)
  el.selectAll('*').remove()

  const width = svg.clientWidth || 800
  const height = svg.clientHeight || 600

  const { nodes, links, neighbours, minYear, maxYear } = meta

  // Time axis → vertical band. Leave 60px padding top/bottom so labels don't
  // bleed off. Persons with no birth year float in the middle.
  const hasTime = minYear != null && maxYear != null && maxYear !== minYear
  const yScale = hasTime
    ? d3.scaleLinear().domain([minYear, maxYear]).range([70, height - 70])
    : null
  const targetY = (n) => {
    if (!hasTime) return height / 2
    if (n.birthYear == null) return height / 2
    return yScale(n.birthYear)
  }

  // Centrality → radius. sqrt so one hub doesn't dwarf everyone.
  const maxDeg = Math.max(1, ...nodes.map(n => n.degree))
  const rScale = d3.scaleSqrt().domain([0, maxDeg]).range([NODE_RADIUS_MIN, NODE_RADIUS_MAX])
  const nodeR = (n) => rScale(n.degree)

  // Layer group — what d3.zoom transforms.
  const root = el.append('g').attr('class', 'node-map__root')

  // Century reference lines behind everything (only when we have a time span)
  if (hasTime) {
    const startCentury = Math.floor(minYear / 100) * 100
    const endCentury = Math.ceil(maxYear / 100) * 100
    const lines = []
    for (let y = startCentury; y <= endCentury; y += 50) {
      if (y < minYear - 20 || y > maxYear + 20) continue
      lines.push(y)
    }
    const bg = root.append('g').attr('class', 'node-map__timelines')
    bg.selectAll('line').data(lines).join('line')
      .attr('x1', 0).attr('x2', width)
      .attr('y1', y => yScale(y)).attr('y2', y => yScale(y))
      .attr('stroke', '#e5e0d5')
      .attr('stroke-dasharray', '3 5')
    bg.selectAll('text').data(lines).join('text')
      .attr('x', 12).attr('y', y => yScale(y) - 4)
      .attr('fill', '#94a3b8')
      .attr('font-size', 11)
      .attr('font-family', 'Georgia, serif')
      .text(y => y)
  }

  const linkLayer  = root.append('g').attr('class', 'node-map__links')
  const nodeLayer  = root.append('g').attr('class', 'node-map__nodes')
  const labelLayer = root.append('g').attr('class', 'node-map__labels')

  // Forces. Y pinned by time axis (strong), X is free force-directed.
  const chargeStrength = -160 - Math.min(600, nodes.length * 8)
  const linkDistance = (l) => l.type === 'partner' ? 40 : 90

  const simulation = d3.forceSimulation(nodes)
    .force('link', d3.forceLink(links).id(d => d.id).distance(linkDistance).strength(0.9))
    .force('charge', d3.forceManyBody().strength(chargeStrength))
    .force('x', d3.forceX(width / 2).strength(0.04))
    .force('y', d3.forceY(d => targetY(d)).strength(hasTime ? 0.55 : 0.04))
    .force('collide', d3.forceCollide(d => nodeR(d) + COLLIDE_PADDING).strength(0.95))
    .alphaDecay(0.035)

  const link = linkLayer
    .selectAll('line')
    .data(links)
    .join('line')
    .attr('stroke', d => d.type === 'partner' ? '#c8a56e' : '#94a3b8')
    .attr('stroke-width', d => d.type === 'partner' ? 2 : 1.25)
    .attr('stroke-opacity', 0.7)

  const node = nodeLayer
    .selectAll('circle')
    .data(nodes)
    .join('circle')
    .attr('r', d => nodeR(d))
    .attr('fill', d => d.gender === 'male' ? '#3b6b5a' : d.gender === 'female' ? '#c8a56e' : '#9ca3af')
    .attr('stroke', d => SIDE_STROKE[d.side] || '#ffffff')
    .attr('stroke-width', d => d.side === 'self' ? 3.5 : 2.5)
    .attr('cursor', 'pointer')
    .on('click', (event, d) => handleNodeClick(event, d))
    .on('mouseover', (event, d) => highlightNeighbourhood(d.id))
    .on('mouseout', resetHighlight)
    .call(d3.drag()
      .on('start', (event, d) => { if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y })
      .on('drag',  (event, d) => { d.fx = event.x; d.fy = event.y })
      .on('end',   (event, d) => { if (!event.active) simulation.alphaTarget(0); d.fx = null; d.fy = null })
    )

  const label = labelLayer
    .selectAll('text')
    .data(nodes)
    .join('text')
    .text(d => d.label)
    .attr('font-size', 11)
    .attr('text-anchor', 'middle')
    .attr('dy', d => nodeR(d) + 14)
    .attr('fill', '#1e293b')
    .attr('pointer-events', 'none')
    .attr('opacity', 0)

  // Search highlight — matches stay fully lit, misses dim to 0.12.
  const matches = searchMatches(nodes, search)
  if (search) {
    node.attr('opacity', d => matches.has(d.id) ? 1 : 0.15)
    label.attr('opacity', d => matches.has(d.id) ? 1 : 0)
    link.attr('stroke-opacity', l =>
      matches.has(l.source.id || l.source) || matches.has(l.target.id || l.target) ? 0.9 : 0.08
    )
  }

  // ── Hover highlight ─────────────────────────────────────────────────────
  function highlightNeighbourhood(focalId) {
    if (search) return // don't stomp search
    const near = neighbours.get(focalId) || new Set()
    node.attr('opacity', d => (d.id === focalId || near.has(d.id)) ? 1 : 0.2)
    link.attr('stroke-opacity', l =>
      ((l.source.id || l.source) === focalId || (l.target.id || l.target) === focalId) ? 0.95 : 0.08
    )
    label.attr('opacity', d => {
      if (d.id === focalId) return 1
      if (near.has(d.id)) return 0.9
      return 0
    })
  }

  function resetHighlight() {
    if (search) return
    node.attr('opacity', 1)
    link.attr('stroke-opacity', 0.7)
    label.attr('opacity', currentZoom >= 1.4 ? 0.9 : 0)
  }

  // ── Path-find click handler ─────────────────────────────────────────────
  let routeAnchor = null
  function handleNodeClick(event, d) {
    if (!routing && !event.shiftKey) { onPersonClick?.(d.id); return }
    if (!routeAnchor) {
      routeAnchor = d.id
      onRouteMessage?.(`From "${d.label}" — click the destination…`)
      node.attr('stroke-width', n => n.id === d.id ? 4.5 : n.side === 'self' ? 3.5 : 2.5)
      return
    }
    const path = shortestPath(meta, routeAnchor, d.id)
    if (!path) {
      onRouteMessage?.('No path between them in this tree.')
    } else {
      highlightPath(path)
      onRouteMessage?.(`${path.length - 1} step${path.length === 2 ? '' : 's'} between them.`)
    }
    routeAnchor = null
  }

  function highlightPath(pathIds) {
    const onPath = new Set(pathIds)
    const pairs = new Set()
    for (let i = 0; i < pathIds.length - 1; i++) {
      pairs.add(`${pathIds[i]}|${pathIds[i + 1]}`)
      pairs.add(`${pathIds[i + 1]}|${pathIds[i]}`)
    }
    node.attr('opacity', d => onPath.has(d.id) ? 1 : 0.15)
    link.attr('stroke-opacity', l => {
      const s = l.source.id || l.source, t = l.target.id || l.target
      return pairs.has(`${s}|${t}`) ? 1 : 0.05
    })
    label.attr('opacity', d => onPath.has(d.id) ? 1 : 0)
  }

  // ── Zoom / pan ──────────────────────────────────────────────────────────
  let currentZoom = 1
  const zoom = d3.zoom()
    .scaleExtent([0.25, 4])
    .on('zoom', (event) => {
      root.attr('transform', event.transform)
      currentZoom = event.transform.k
      if (!search) label.attr('opacity', currentZoom >= 1.4 ? 0.9 : 0)
    })
  el.call(zoom)
  el.on('dblclick.zoom', () => el.transition().duration(350).call(zoom.transform, d3.zoomIdentity))

  simulation.on('tick', () => {
    link.attr('x1', d => d.source.x).attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x).attr('y2', d => d.target.y)
    node.attr('cx', d => d.x).attr('cy', d => d.y)
    label.attr('x', d => d.x).attr('y', d => d.y)
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function parseYear(dateStr) {
  if (!dateStr) return null
  const m = String(dateStr).match(/\b(1[0-9]{3}|20[0-2][0-9])\b/)
  return m ? parseInt(m[1], 10) : null
}

function searchMatches(nodes, query) {
  if (!query) return new Set()
  const q = query.toLowerCase()
  const hits = new Set()
  for (const n of nodes) {
    if (n.label.toLowerCase().includes(q)) hits.add(n.id)
  }
  return hits
}

/**
 * Plain BFS shortest path over the undirected graph. Returns an array of
 * person ids from->to, or null if they aren't connected.
 */
function shortestPath(meta, fromId, toId) {
  if (fromId === toId) return [fromId]
  const prev = new Map([[fromId, null]])
  const queue = [fromId]
  while (queue.length) {
    const cur = queue.shift()
    for (const edge of meta.adj.get(cur) || []) {
      if (prev.has(edge.neighbour)) continue
      prev.set(edge.neighbour, cur)
      if (edge.neighbour === toId) {
        // Reconstruct.
        const path = [toId]
        let p = cur
        while (p != null) { path.push(p); p = prev.get(p) }
        return path.reverse()
      }
      queue.push(edge.neighbour)
    }
  }
  return null
}
